import { loanApi } from "@api/loans";
import { fineApi } from "@api/fine/fine";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { invenioConfig } from "@config";
import PaymentSubmissionModal from "./Modal/PaymentSubmissionModal/PaymentSubmissionModal";
import OverdueSlipModal from "./Modal/OverdueSlipModal/OverdueSlipModal";
import SearchFilters from "./FeeDisplay/SearchFilters";
import FilterNotification from "./FeeDisplay/FilterNotification";
import FineManagementTable from "./FeeTable/FineManagementTable";
import FineHistoryTab from "./FeeTable/FineHistoryTab";
import React, { Component } from "react";
import { Container, Message, Tab } from "semantic-ui-react";
import PaymentHistoryModal from "./Modal/PaymentHistoryModal/PaymentHistoryModal";
import ExportDataControls from "./ExportDataControls";
import YearFilter from "./FeeDisplay/YearFilter";
import { patronApi } from "@api/patrons";
import {
  checkLoanStatus,
  processFineData,
  extractFormData,
  generatePaymentResult,
  debounce,
  calculateOverdueDays,
  calculateBusinessDays,
  FINE_RATE_PER_DAY,
  SUCCESS_MESSAGE_DURATION,
  SEARCH_DEBOUNCE_DELAY,
  REFRESH_DELAY_AFTER_RETURN,
  REFRESH_DELAY_AFTER_PAYMENT,
} from "@utils/fineUtils";

const YEAR_RANGE_BUFFER = 2;

export class FineManagementList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: { hits: [], total: 0 },
      historyData: { hits: [], total: 0 },
      isLoading: true,
      isLoadingHistory: false,
      error: {},
      searchQuery: "",
      historySearchQuery: "",
      showPaymentModal: false,
      selectedPatron: null,
      showOverdueSlipModal: false,
      selectedPatronForSlip: null,
      urlPatronId: null,
      urlPatronName: null,
      urlLoanId: null,
      isFilteredByUrl: false,
      showPaymentHistoryModal: false,
      selectedPatronForHistory: null,
      patronOverrideData: {},
      selectedYear: null,
      historySelectedYear: null,
      syncInProgress: false,
      lastReturnResults: null,
      showReturnSuccessMessage: false,
      activeTabIndex: 0,
    };

    this.searchTimeout = null;
    this.historySearchTimeout = null;
    this.debouncedSync = debounce(
      this.syncAndFetchFines,
      SEARCH_DEBOUNCE_DELAY
    );
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this.extractUrlParameters();
    this.syncAndFetchFines();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.cancellableFetch && this.cancellableFetch.cancel();
    clearTimeout(this.searchTimeout);
    clearTimeout(this.historySearchTimeout);
  }

  calculateLoanFine = (loan, fineRatePerDay = FINE_RATE_PER_DAY) => {
    if (!loan?.metadata?.end_date) return 0;

    const dueDate = new Date(loan.metadata.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    if (today <= dueDate) return 0;

    // Use centralized business days calculation
    const businessDays = calculateBusinessDays(dueDate, today);

    return businessDays * fineRatePerDay;
  };

  calculateOverdueDaysFromDate = (dueDateStr) => {
    if (!dueDateStr) return 0;

    const dueDate = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    if (today <= dueDate) return 0;

    return calculateBusinessDays(dueDate, today);
  };

  // Process fine history data for display in the history table
  processFineHistoryData = (fines) => {
    return (
      fines
        // Filter out active fines first
        .filter((fine) => fine.status && fine.status.toLowerCase() !== "active")
        .map((fine) => ({
          id: fine.id,
          loan_id: fine.loan_id,
          patron_name: fine.patron_name || fine.name,
          patron_email: fine.patron_email || fine.email,
          patron_pid: fine.patron_pid,
          document_title: fine.document_title || fine.item_title,
          item_barcode: fine.item_barcode,
          due_date: fine.due_date || fine.start_date,
          return_date: fine.return_date,
          total_fine: parseFloat(fine.total_fine || 0),
          calculated_fine: parseFloat(fine.calculated_fine || 0),
          status: fine.status
            ? fine.status.charAt(0).toUpperCase() +
              fine.status.slice(1).toLowerCase()
            : "",
          created_date: fine.created_date,
          updated_date: fine.updated_date,
          created_by: fine.created_by,
          updated_by: fine.updated_by,
          payment_reference: fine.payment_reference,
          notes: fine.notes,
          days_overdue: fine.due_date ? calculateOverdueDays(fine.due_date) : 0,
          is_resolved: ["resolved", "paid", "waived", "cancelled"].includes(
            fine.status
          ),
        }))
        .sort((a, b) => {
          return (
            new Date(b.updated_date || b.created_date) -
            new Date(a.updated_date || a.created_date)
          );
        })
    );
  };

  // Safe setState that checks if component is mounted
  safeSetState = (newState) => {
    if (this._isMounted) {
      this.setState(newState);
    }
  };

  // Data validation helper
  validateLoanData = (loan) => {
    if (!loan || !loan.metadata) {
      return false;
    }

    if (!loan.metadata.pid) {
      return false;
    }

    if (!loan.metadata.patron || !loan.metadata.patron.pid) {
      return false;
    }

    return true;
  };

  // Main data sync and fetch operations
  syncAndFetchFines = async () => {
    try {
      this.safeSetState({ isLoading: true, syncInProgress: true });

      const { isFilteredByUrl, urlPatronId } = this.state;

      // Fetch current overdue loans
      const overdueLoans = await this.fetchOverdueLoans();
      // Create a set of currently overdue loan IDs for quick lookup
      const currentOverdueLoanIds = new Set(
        overdueLoans
          .filter(this.validateLoanData)
          .map((loan) => loan.metadata.pid)
      );

      if (isFilteredByUrl && urlPatronId) {
        if (overdueLoans.length > 0) {
          await this.syncLoansWithFines(overdueLoans);
        }

        const activeFines = await fineApi.getActiveFines();

        // Reactivate waived fines for currently overdue loans
        await this.reactivateWaivedFinesForOverdueLoans(
          activeFines.data.hits,
          currentOverdueLoanIds
        );

        // Cleanup stale active fines
        await this.cleanupStaleActiveFines(
          activeFines.data.hits,
          currentOverdueLoanIds
        );

        await this.fetchFinesFromDatabase();
      } else {
        await this.syncLoansWithFines(overdueLoans);

        const activeFines = await fineApi.getActiveFines();

        // Reactivate waived fines for currently overdue loans
        await this.reactivateWaivedFinesForOverdueLoans(
          activeFines.data.hits,
          currentOverdueLoanIds
        );

        // Cleanup stale active fines
        await this.cleanupStaleActiveFines(
          activeFines.data.hits,
          currentOverdueLoanIds
        );

        await this.fetchFinesFromDatabase();
      }
    } catch (error) {
      this.safeSetState({ error, isLoading: false });
    } finally {
      this.safeSetState({ syncInProgress: false });
    }
  };

  reactivateWaivedFinesForOverdueLoans = async (
    allFines,
    currentOverdueLoanIds
  ) => {
    const waivedButOverdue = allFines.filter(
      (fine) =>
        fine.status === "waived" &&
        fine.loan_id &&
        currentOverdueLoanIds.has(fine.loan_id)
    );

    for (const fine of waivedButOverdue) {
      const loanStatus = await checkLoanStatus(fine.loan_id);

      if (
        loanStatus === "ITEM_ON_LOAN" &&
        currentOverdueLoanIds.has(fine.loan_id)
      ) {
        const loanResponse = await loanApi.get(fine.loan_id);
        const loan = loanResponse.data;

        const calculatedFine = this.calculateLoanFine(loan);

        await fineApi.update(fine.id, {
          status: "active",
          total_fine: calculatedFine,
          calculated_fine: calculatedFine,
          notes: `Reactivated - loan is overdue again. Fine recalculated to ${calculatedFine} on ${new Date().toISOString()}`,
          updated_by: "system",
        });
      }
    }
  };

  cleanupStaleActiveFines = async (activeFines, currentOverdueLoanIds) => {
    const staleFines = activeFines.filter(
      (fine) =>
        fine.loan_id &&
        !currentOverdueLoanIds.has(fine.loan_id) &&
        fine.status !== "waived" &&
        fine.status !== "resolved" &&
        fine.status !== "cancelled" &&
        fine.status !== "paid"
    );

    for (const fine of staleFines) {
      const loanStatus = await checkLoanStatus(fine.loan_id);

      if (loanStatus === "ITEM_RETURNED") {
        if (fine.status === "paid") {
          await fineApi.update(fine.id, {
            status: "resolved",
            notes: `Automatically resolved - item returned after payment on ${new Date().toISOString()}`,
            updated_by: "system",
          });
        } else if (fine.status === "active" || fine.status === "unpaid") {
          await fineApi.waive(fine.id, {
            notes: "Automatically waived - item returned without payment",
            updated_by: "system",
          });
        }
      } else if (loanStatus === "ITEM_ON_LOAN") {
        // Item is on loan but NOT overdue (extended or due date changed)
        await fineApi.waive(fine.id, {
          notes:
            "Automatically waived - loan no longer overdue (extended or date changed)",
          updated_by: "system",
        });
      }
    }
  };

  // fetch fine history
  fetchFineHistory = async () => {
    try {
      this.safeSetState({ isLoadingHistory: true });

      const { urlPatronId } = this.state;

      // Fetch all fines using existing API methods
      let response;
      if (urlPatronId) {
        response = await fineApi.getUserFines(urlPatronId);
      } else {
        const queryParams = fineApi.query().sortByNewest().qs();
        response = await fineApi.list(queryParams);
      }

      let allFines = response.data.hits || [];

      const processedHistoryData = this.processFineHistoryData(allFines);

      this.safeSetState({
        historyData: {
          hits: processedHistoryData,
          total: processedHistoryData.length,
        },
        isLoadingHistory: false,
      });
    } catch (error) {
      this.safeSetState({
        error: { ...this.state.error, history: error },
        isLoadingHistory: false,
      });
    }
  };

  fetchOverdueLoans = async () => {
    const { urlPatronId, isFilteredByUrl } = this.state;

    let query = loanApi
      .query()
      .overdue()
      .withState(invenioConfig.CIRCULATION.loanActiveStates);

    // Only filter by patron if we have a URL patron ID
    if (isFilteredByUrl && urlPatronId) {
      query = query.withPatronPid(urlPatronId);
    }

    const response = await loanApi.list(query.qs());
    const validLoans = response.data.hits.filter(this.validateLoanData);

    return validLoans;
  };

  syncLoansWithFines = async (loans) => {
    if (!loans?.length) return;

    const validLoans = loans.filter(this.validateLoanData);

    const syncData = {
      loans: validLoans.map((loan) => ({
        metadata: {
          pid: loan.metadata.pid,
          patron: {
            email: loan.metadata.patron?.email || "",
            name: loan.metadata.patron?.name || "Unknown",
            pid: loan.metadata.patron?.pid,
          },
          document: {
            title: loan.metadata.document?.title || "Unknown Document",
          },
          item: {
            barcode: loan.metadata.item?.barcode || "",
          },
          end_date: loan.metadata.end_date,
        },
      })),
    };

    return await fineApi.sync(syncData);
  };

  cleanupReturnedLoanFines = async (currentOverdueLoans, activeFines) => {
    // Filter out loans with invalid PIDs
    const validOverdueLoans = currentOverdueLoans.filter(this.validateLoanData);

    const currentOverdueLoanIds = new Set(
      validOverdueLoans.map((loan) => loan.metadata.pid)
    );

    const finesForReturnedLoans = activeFines.filter(
      (fine) => fine.loan_id && !currentOverdueLoanIds.has(fine.loan_id)
    );

    for (const fine of finesForReturnedLoans) {
      try {
        const loanStatus = await checkLoanStatus(fine.loan_id);

        if (loanStatus === "ITEM_RETURNED") {
          if (fine.status === "paid") {
            await fineApi.update(fine.id, {
              status: "resolved",
              notes: `Automatically resolved - item returned after payment on ${new Date().toISOString()}`,
              updated_by: "system",
            });
          } else if (fine.status === "active" || fine.status === "unpaid") {
            await fineApi.waive(fine.id, {
              notes: "Automatically waived - item returned without payment",
              updated_by: "system",
            });
          }
        }
      } catch (error) {
        // Silent error handling - continue processing other fines
      }
    }
  };

  fetchFinesFromDatabase = async () => {
    try {
      const { urlPatronId, urlPatronName, isFilteredByUrl } = this.state;

      const response = await fineApi.getActiveFines();

      let filteredFines = response.data.hits.filter(
        (fine) =>
          fine.status !== "resolved" &&
          fine.status !== "cancelled" &&
          fine.status !== "waived"
      );

      if (isFilteredByUrl && urlPatronId) {
        const patronFines = filteredFines.filter(
          (fine) =>
            fine.patron_pid === urlPatronId ||
            fine.patron_pid === parseInt(urlPatronId)
        );

        if (patronFines.length > 0) {
          // Process patron fines normally
          const processedData = processFineData(
            filteredFines,
            this.calculateLoanFine
          );
          const validatedProcessedData = processedData
            .map((patron) => {
              let totalFine = patron.totalFine;
              if (totalFine === 0 && patron.loans.length > 0) {
                totalFine = patron.loans.reduce(
                  (sum, loan) => sum + (loan.calculatedFine || 0),
                  0
                );
              }

              return {
                ...patron,
                totalFine,
                loans: patron.loans.filter(this.validateLoanData),
              };
            })
            .filter((patron) => patron.loans.length > 0);

          this.safeSetState({
            data: {
              hits: validatedProcessedData,
              total: validatedProcessedData.length,
            },
            isLoading: false,
            error: {},
          });
        } else {
          // No fines found - fetch actual overdue loans for this patron
          try {
            const overdueLoans = await this.fetchOverdueLoans();
            const patronOverdueLoans = overdueLoans.filter(
              (loan) =>
                loan.metadata?.patron?.pid === urlPatronId ||
                loan.metadata?.patron?.pid === parseInt(urlPatronId)
            );

            if (patronOverdueLoans.length > 0) {
              const patron = patronOverdueLoans[0].metadata.patron;
              const totalCalculatedFine = patronOverdueLoans.reduce(
                (total, loan) => {
                  return total + this.calculateLoanFine(loan);
                },
                0
              );

              const patronWithLoans = {
                pid: urlPatronId,
                name: patron.name || urlPatronName || `Patron ${urlPatronId}`,
                email: patron.email || "Email not available",
                user_type: patron.user_type,
                patron_type: patron.patron_type,
                role: patron.role,
                totalFine: totalCalculatedFine,
                overdueCount: patronOverdueLoans.length,
                loans: patronOverdueLoans.map((loan) => {
                  const calculatedFine = this.calculateLoanFine(loan);
                  return {
                    ...loan,
                    calculatedFine: calculatedFine,
                  };
                }),
                fineRecords: [],
              };

              this.safeSetState({
                data: {
                  hits: [patronWithLoans],
                  total: 1,
                },
                isLoading: false,
                error: {},
              });
            } else {
              const dummyPatron = {
                pid: urlPatronId,
                name: urlPatronName || `Patron ${urlPatronId}`,
                email: "Email not available",
                totalFine: 0,
                overdueCount: 0,
                loans: [],
                fineRecords: [],
              };

              this.safeSetState({
                data: {
                  hits: [dummyPatron],
                  total: 1,
                },
                isLoading: false,
                error: {},
              });
            }
          } catch (error) {
            const dummyPatron = {
              pid: urlPatronId,
              name: urlPatronName || `Patron ${urlPatronId}`,
              email: "Email not available",
              totalFine: 0,
              overdueCount: 0,
              loans: [],
              fineRecords: [],
            };

            this.safeSetState({
              data: {
                hits: [dummyPatron],
                total: 1,
              },
              isLoading: false,
              error: {},
            });
          }
        }
      } else {
        // Group fines by patron email
        const patronMap = new Map();

        filteredFines.forEach((fine) => {
          const patronKey = fine.email;

          if (!patronMap.has(patronKey)) {
            patronMap.set(patronKey, {
              pid: fine.email,
              name: fine.name,
              email: fine.email,
              user_type: fine.user_type || fine.patron_type || fine.role,
              patron_type: fine.patron_type,
              role: fine.role,
              totalFine: 0,
              overdueCount: 0,
              loans: [],
              fineRecords: [],
            });
          }

          const patron = patronMap.get(patronKey);

          // Add to fine records
          patron.fineRecords.push(fine);

          const pseudoLoan = {
            metadata: {
              pid: fine.loan_id || fine.loanId,
              patron: {
                pid: fine.email,
                name: fine.name,
                email: fine.email,
                user_type: fine.user_type,
                patron_type: fine.patron_type,
                role: fine.role,
              },
              document: {
                title: fine.item_title || fine.itemTitle || "Unknown Document",
              },
              item: {
                barcode: fine.item_barcode || fine.itemBarcode || "",
              },
              end_date: fine.start_date || fine.startDate,
            },
            calculatedFine: parseFloat(fine.total_fine || fine.totalFine || 0),
            overdue_days: this.calculateOverdueDaysFromDate(
              fine.start_date || fine.startDate
            ),
          };

          patron.loans.push(pseudoLoan);
          patron.totalFine += parseFloat(
            fine.total_fine || fine.totalFine || 0
          );
          patron.overdueCount += 1;
        });

        const validatedProcessedData = Array.from(patronMap.values()).filter(
          (patron) => patron.loans.length > 0
        );

        const patronEmails = validatedProcessedData.map((p) => p.email);
        const patronData = await this.fetchPatronTypesForFines(patronEmails);

        // Assign user types AND user IDs to each patron
        const dataWithTypes = validatedProcessedData.map((patron) => ({
          ...patron,
          user_type: patronData[patron.email]?.userType || "student",
          patron_type: patronData[patron.email]?.userType || "student",
          pid: patronData[patron.email]?.userId,
        }));

        this.safeSetState({
          data: {
            hits: dataWithTypes,
            total: dataWithTypes.length,
          },
          isLoading: false,
          error: {},
        });
      }
    } catch (error) {
      this.safeSetState({ error, isLoading: false });
    }
  };

  fetchPatronTypesForFines = async (patronEmails) => {
    const patronData = {};

    try {
      const patronPromises = patronEmails.map(async (email) => {
        try {
          const query = patronApi.query().withEmail(email).qs();
          const response = await patronApi.list(query, false);

          if (response.data.hits && response.data.hits.length > 0) {
            const patron = response.data.hits[0];

            // Extract user type
            let userType = "student";
            if (patron.metadata) {
              if (patron.metadata.user_type) {
                userType = patron.metadata.user_type.trim().toLowerCase();
              } else if (patron.metadata.patron_type) {
                userType = patron.metadata.patron_type.trim().toLowerCase();
              } else if (patron.metadata.role) {
                userType = patron.metadata.role.trim().toLowerCase();
              }
            }

            // Return both user_id and userType
            return {
              email,
              userType,
              userId: patron.metadata.id, // This is the numeric user ID
            };
          } else {
            return { email, userType: "student", userId: null };
          }
        } catch (error) {
          return { email, userType: "student", userId: null };
        }
      });

      const results = await Promise.all(patronPromises);
      results.forEach(({ email, userType, userId }) => {
        patronData[email] = { userType, userId };
      });
    } catch (error) {
      console.error("Error fetching patron data:", error);
    }

    return patronData;
  };

  // Event handlers
  handleLoanStatusUpdate = async (returnedLoanIds) => {
    try {
      const cleanupResponse = await fineApi.cleanupReturnedLoans(
        returnedLoanIds
      );
      const isSuccess = cleanupResponse.data.status === "success";

      this.safeSetState({
        lastReturnResults: {
          success: true,
          returnedLoans: returnedLoanIds,
          updatedFines: isSuccess
            ? cleanupResponse.data.data.updated_fines
            : [],
          timestamp: new Date(),
          count: returnedLoanIds.length,
          fineCleanup: isSuccess
            ? cleanupResponse.data.data.summary
            : undefined,
          error: !isSuccess ? "Fine cleanup failed" : undefined,
        },
        showReturnSuccessMessage: true,
      });
    } catch {
      this.safeSetState({
        lastReturnResults: {
          success: true,
          returnedLoans: returnedLoanIds,
          timestamp: new Date(),
          count: returnedLoanIds.length,
          error: "Fine cleanup failed",
        },
        showReturnSuccessMessage: true,
      });
    }

    setTimeout(() => {
      this.safeSetState({ showReturnSuccessMessage: false });
    }, SUCCESS_MESSAGE_DURATION);

    this.refreshDataAfterReturn(returnedLoanIds);
  };

  refreshDataAfterReturn = async (returnedLoanIds) => {
    this.updateLocalStateAfterReturn(returnedLoanIds);
    setTimeout(async () => {
      if (this._isMounted) {
        await this.syncAndFetchFines();
        if (this.state.historyData.hits.length > 0) {
          await this.fetchFineHistory();
        }
      }
    }, REFRESH_DELAY_AFTER_RETURN);
  };

  updateLocalStateAfterReturn = (returnedLoanIds) => {
    const { data } = this.state;

    const updatedHits = data.hits
      .map((patron) => {
        const updatedLoans = patron.loans.filter(
          (loan) =>
            loan.metadata?.pid && !returnedLoanIds.includes(loan.metadata.pid)
        );

        const updatedFineRecords = patron.fineRecords.filter(
          (fine) => !returnedLoanIds.includes(fine.loan_id)
        );

        const newTotalFine = updatedFineRecords.reduce(
          (total, fine) => total + parseFloat(fine.total_fine || 0),
          0
        );

        return {
          ...patron,
          loans: updatedLoans,
          fineRecords: updatedFineRecords,
          totalFine: newTotalFine,
          overdueCount: updatedLoans.length,
        };
      })
      .filter((patron) => patron.loans.length > 0);

    this.safeSetState({
      data: { ...data, hits: updatedHits, total: updatedHits.length },
    });
  };

  handlePaymentSubmit = async (formData) => {
    const extractedData = extractFormData(formData);
    const { selectedLoanIds, patronId, amount, paymentReceipt } = extractedData;

    const patron = this.state.data.hits.find((p) => p.pid === patronId);
    if (!patron) {
      throw new Error("Patron not found");
    }

    const results = await this.processFinPayments(
      patron.fineRecords,
      selectedLoanIds,
      amount,
      paymentReceipt
    );

    setTimeout(() => {
      if (this._isMounted) {
        this.syncAndFetchFines();
        if (this.state.historyData.hits.length > 0) {
          this.fetchFineHistory();
        }
      }
    }, REFRESH_DELAY_AFTER_PAYMENT);

    return generatePaymentResult(results);
  };

  processFinPayments = async (
    fineRecords,
    selectedLoanIds,
    amount,
    paymentReceipt
  ) => {
    const processedFines = [];
    const failedFines = [];

    for (const fineRecord of fineRecords) {
      if (selectedLoanIds.includes(fineRecord.loan_id)) {
        try {
          const result = await fineApi.pay(fineRecord.id, {
            payment_reference: paymentReceipt,
            notes: `Payment of ${amount} for overdue fine`,
          });

          processedFines.push({
            fineId: fineRecord.id,
            status: "success",
            result,
          });
        } catch (error) {
          failedFines.push({
            fineId: fineRecord.id,
            status: "failed",
            error: error.message || "Unknown error",
          });
        }
      }
    }

    return { processedFines, failedFines };
  };

  // Filter and search methods
  getAvailableYears = (dataType = "active") => {
    const data =
      dataType === "history" ? this.state.historyData : this.state.data;

    // Return empty array if no data
    if (!data.hits || data.hits.length === 0) return [];

    let yearsWithData;

    if (dataType === "history") {
      yearsWithData = data.hits
        .map((fine) => {
          const dateToCheck =
            fine.updated_date || fine.created_date || fine.due_date;
          return dateToCheck ? new Date(dateToCheck).getFullYear() : null;
        })
        .filter((year) => year !== null && !isNaN(year));
    } else {
      yearsWithData = data.hits
        .flatMap((patron) =>
          patron.loans.map((loan) =>
            new Date(loan.metadata.end_date).getFullYear()
          )
        )
        .filter((year) => !isNaN(year));
    }

    if (yearsWithData.length === 0) return [];

    const uniqueYears = [...new Set(yearsWithData)];
    const minYear = Math.min(...uniqueYears) - YEAR_RANGE_BUFFER;
    const maxYear = Math.max(...uniqueYears) + YEAR_RANGE_BUFFER;

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
  };

  getFilteredDataByYearAndSearch = () => {
    const {
      data,
      selectedYear,
      searchQuery,
      isFilteredByUrl,
      urlPatronId,
      urlPatronName,
    } = this.state;
    let filteredData = data.hits;

    // Apply URL-based filtering first (for overdue redirection)
    if (isFilteredByUrl) {
      if (urlPatronId) {
        filteredData = filteredData.filter(
          (patron) => patron.pid === urlPatronId
        );
      }
      if (urlPatronName && filteredData.length === 0) {
        // If no match by ID, try matching by name (case-insensitive)
        filteredData = data.hits.filter(
          (patron) =>
            patron.name &&
            patron.name.toLowerCase().includes(urlPatronName.toLowerCase())
        );
      }
    }

    // Apply year filter
    if (selectedYear) {
      filteredData = filteredData
        .map((patron) => {
          const filteredLoans = patron.loans.filter((loan) => {
            const year = new Date(loan.metadata.end_date).getFullYear();
            return year === selectedYear;
          });

          return filteredLoans.length > 0
            ? { ...patron, loans: filteredLoans }
            : null;
        })
        .filter(Boolean);
    }

    // Apply search filter (only if not filtered by URL)
    if (searchQuery && !isFilteredByUrl) {
      const searchLower = searchQuery.toLowerCase();
      filteredData = filteredData.filter(
        (patron) =>
          patron.name?.toLowerCase().includes(searchLower) ||
          patron.email?.toLowerCase().includes(searchLower) ||
          patron.loans?.some(
            (loan) =>
              loan.metadata?.document?.title
                ?.toLowerCase()
                .includes(searchLower) ||
              loan.metadata?.item?.barcode?.toLowerCase().includes(searchLower)
          )
      );
    }

    return filteredData;
  };

  getFilteredHistoryDataByYearAndStatus = () => {
    const { historyData, historySelectedYear, historySearchQuery } = this.state;

    // Start with all history data (active already filtered out in processFineHistoryData)
    let filteredData = historyData.hits;

    if (historySearchQuery) {
      const searchLower = historySearchQuery.toLowerCase();
      filteredData = filteredData.filter(
        (fine) =>
          fine.patron_name?.toLowerCase().includes(searchLower) ||
          fine.patron_email?.toLowerCase().includes(searchLower) ||
          fine.document_title?.toLowerCase().includes(searchLower) ||
          fine.item_barcode?.toLowerCase().includes(searchLower) ||
          fine.status?.toLowerCase().includes(searchLower) ||
          fine.payment_reference?.toLowerCase().includes(searchLower)
      );
    }

    if (historySelectedYear) {
      filteredData = filteredData.filter((fine) => {
        // Check the same date fields as getAvailableYears for consistency
        const dateToCheck =
          fine.updated_date || fine.created_date || fine.due_date;
        if (!dateToCheck) return false;

        const year = new Date(dateToCheck).getFullYear();
        return year === historySelectedYear;
      });
    }

    return filteredData;
  };

  handleYearChange = (year, isHistory = false) => {
    const stateKey = isHistory ? "historySelectedYear" : "selectedYear";
    this.safeSetState({ [stateKey]: year !== null ? Number(year) : null });
  };

  handleSearchInputChange = (e, { value }, isHistory = false) => {
    const stateKey = isHistory ? "historySearchQuery" : "searchQuery";
    this.safeSetState({ [stateKey]: value });
  };

  handleClearFilters = (isHistory = false) => {
    if (isHistory) {
      this.safeSetState({
        historySearchQuery: "",
        historySelectedYear: null,
      });
    } else {
      this.safeSetState({ searchQuery: "", selectedYear: null });
    }
  };

  // Tab change handler
  handleTabChange = (e, { activeIndex }) => {
    this.safeSetState({ activeTabIndex: activeIndex });

    // Load history data when switching to history tab
    if (activeIndex === 1 && this.state.historyData.hits.length === 0) {
      this.fetchFineHistory();
    }
  };

  // URL and storage methods
  extractUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const patronId = urlParams.get("patron");
    const patronName = urlParams.get("patronName");
    const loanId = urlParams.get("loan");

    if (patronId || loanId || patronName) {
      this.safeSetState({
        urlPatronId: patronId,
        urlPatronName: decodeURIComponent(patronName || ""),
        urlLoanId: loanId,
        isFilteredByUrl: true,
      });
    }
  };

  clearUrlFilters = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    this.setState(
      {
        urlPatronId: null,
        urlPatronName: null,
        urlLoanId: null,
        isFilteredByUrl: false,
      },
      () => {
        this.syncAndFetchFines();
        if (this.state.historyData.hits.length > 0) {
          this.fetchFineHistory();
        }
      }
    );
  };

  // Modal handlers
  openPaymentModal = (patron) => {
    this.safeSetState({
      showPaymentModal: true,
      selectedPatron: patron,
    });
  };

  closePaymentModal = () => {
    this.safeSetState({ showPaymentModal: false, selectedPatron: null });
  };

  openOverdueSlipModal = (patron) => {
    this.safeSetState({
      showOverdueSlipModal: true,
      selectedPatronForSlip: patron,
    });
  };

  closeOverdueSlipModal = async (overrideData) => {
    this.safeSetState({
      showOverdueSlipModal: false,
      selectedPatronForSlip: null,
    });

    // Refetch data after modal closes
    setTimeout(async () => {
      await this.syncAndFetchFines();
    }, 500); // Small delay to ensure database update completes
  };

  openPaymentModal = (patron) => {
    // Fetch fresh data before opening payment modal
    this.fetchFineDataForPatron(patron);
  };

  fetchFineDataForPatron = async (patron) => {
    try {
      // Fetch fresh fine data from database
      const response = await fineApi.getUserFines(patron.email);

      if (response.data?.hits?.length > 0) {
        const activeFines = response.data.hits.filter(
          (fine) => fine.status === "active" || fine.status === "unpaid"
        );

        // Update the patron's fine records with fresh data
        const updatedLoans = patron.loans.map((loan) => {
          const matchingFine = activeFines.find(
            (fine) => fine.loan_id === loan.metadata.pid
          );

          if (matchingFine) {
            return {
              ...loan,
              calculatedFine: parseFloat(matchingFine.total_fine || 0),
            };
          }
          return loan;
        });

        const updatedPatron = {
          ...patron,
          loans: updatedLoans,
          fineRecords: activeFines,
        };

        this.safeSetState({
          showPaymentModal: true,
          selectedPatron: updatedPatron,
        });
      } else {
        this.safeSetState({
          showPaymentModal: true,
          selectedPatron: patron,
        });
      }
    } catch (error) {
      this.safeSetState({
        showPaymentModal: true,
        selectedPatron: patron,
      });
    }
  };

  openPaymentHistoryModal = (patron) => {
    const patronOverrideData =
      this.state.patronOverrideData[patron.pid] || null;

    this.safeSetState({
      showPaymentHistoryModal: true,
      selectedPatronForHistory: { ...patron, overrideData: patronOverrideData },
    });
  };

  closePaymentHistoryModal = () => {
    this.safeSetState({
      showPaymentHistoryModal: false,
      selectedPatronForHistory: null,
    });
  };

  dismissSuccessMessage = () => {
    this.safeSetState({ showReturnSuccessMessage: false });
  };

  render() {
    const {
      isLoading,
      isLoadingHistory,
      error,
      showPaymentModal,
      selectedPatron,
      showOverdueSlipModal,
      selectedPatronForSlip,
      showPaymentHistoryModal,
      selectedPatronForHistory,
      searchQuery,
      historySearchQuery,
      isFilteredByUrl,
      urlPatronId,
      urlLoanId,
      patronOverrideData,
      syncInProgress,
      showReturnSuccessMessage,
      lastReturnResults,
      selectedYear,
      historySelectedYear,
      activeTabIndex,
    } = this.state;

    const filteredData = this.getFilteredDataByYearAndSearch();
    const filteredHistoryData = this.getFilteredHistoryDataByYearAndStatus();

    const panes = [
      {
        menuItem: {
          key: "active",
          icon: "exclamation triangle",
          content: `Active Fines (${filteredData.length})`,
        },
        render: () => (
          <Tab.Pane>
            {!isFilteredByUrl && (
              <SearchFilters
                searchQuery={searchQuery}
                onSearchInputChange={(e, data) =>
                  this.handleSearchInputChange(e, data, false)
                }
                onClearFilters={() => this.handleClearFilters(false)}
              />
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "1rem",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <YearFilter
                selectedYear={selectedYear}
                onYearChange={(year) => this.handleYearChange(year, false)}
                availableYears={this.getAvailableYears("active")}
              />

              <ExportDataControls
                data={filteredData}
                filenamePrefix="FineManagement"
                reportType="finemanagement"
                reportTitle="Fine Management Export Report"
                columns={[
                  { header: "Patron Name", accessor: "name" },
                  { header: "Email", accessor: "email" },
                  { header: "Total Fine", accessor: "totalFine" },
                  { header: "Overdue Count", accessor: "overdueCount" },
                  {
                    header: "Loans Count",
                    accessor: (row) => row.loans?.length ?? 0,
                  },
                ]}
              />
            </div>

            {syncInProgress && (
              <div
                style={{
                  marginBottom: "1rem",
                  padding: "1rem",
                  backgroundColor: "#f0f8ff",
                  border: "1px solid #0066cc",
                  borderRadius: "4px",
                }}
              >
                <p>
                  <strong>
                    Syncing overdue loans and cleaning up fine records...
                  </strong>
                </p>
              </div>
            )}

            <Loader isLoading={isLoading}>
              <Error error={error}>
                <FineManagementTable
                  data={{ hits: filteredData, total: filteredData.length }}
                  urlLoanId={urlLoanId}
                  fineRatePerDay={FINE_RATE_PER_DAY}
                  onPayFine={this.openPaymentModal}
                  onOverdueSlip={this.openOverdueSlipModal}
                  onViewPaymentHistory={this.openPaymentHistoryModal}
                />
              </Error>
            </Loader>
          </Tab.Pane>
        ),
      },
      {
        menuItem: {
          key: "history",
          icon: "history",
          content: `Fine History (${filteredHistoryData.length})`,
        },
        render: () => (
          <Tab.Pane>
            <SearchFilters
              searchQuery={historySearchQuery}
              onSearchInputChange={(e, data) =>
                this.handleSearchInputChange(e, data, true)
              }
              onClearFilters={() => this.handleClearFilters(true)}
              placeholder="Search by patron name, email, document, or status..."
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "1rem",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <YearFilter
                selectedYear={historySelectedYear}
                onYearChange={(year) => this.handleYearChange(year, true)}
                availableYears={this.getAvailableYears("history")}
              />

              <ExportDataControls
                data={filteredHistoryData}
                filenamePrefix="FineHistory"
                reportType="finehistory"
                reportTitle="Fine History Export Report"
                columns={[
                  { header: "Patron Name", accessor: "patron_name" },
                  { header: "Email", accessor: "patron_email" },
                  { header: "Document Title", accessor: "document_title" },
                  { header: "Item Barcode", accessor: "item_barcode" },
                  { header: "Fine Amount", accessor: "total_fine" },
                  { header: "Status", accessor: "status" },
                  {
                    header: "Payment Reference",
                    accessor: "payment_reference",
                  },
                ]}
              />
            </div>

            <Loader isLoading={isLoadingHistory}>
              <Error error={error.history}>
                <FineHistoryTab
                  data={{
                    hits: filteredHistoryData,
                    total: filteredHistoryData.length,
                  }}
                  fineRatePerDay={FINE_RATE_PER_DAY}
                />
              </Error>
            </Loader>
          </Tab.Pane>
        ),
      },
    ];

    return (
      <Container fluid>
        <FilterNotification
          isFilteredByUrl={isFilteredByUrl}
          urlPatronId={urlPatronId}
          urlPatronName={this.state.urlPatronName}
          urlLoanId={urlLoanId}
          onClearFilters={this.clearUrlFilters}
        />

        {showReturnSuccessMessage && lastReturnResults && (
          <Message
            positive
            onDismiss={this.dismissSuccessMessage}
            style={{ marginBottom: "1rem" }}
          >
            <Message.Header>Fine Paid Successfully!</Message.Header>
            <p style={{ marginTop: "8px", fontSize: "0.9em", color: "#666" }}>
              The fee page will refresh automatically to reflect these changes.
            </p>
          </Message>
        )}

        <Tab
          panes={panes}
          activeIndex={activeTabIndex}
          onTabChange={this.handleTabChange}
          style={{ marginTop: "1rem" }}
        />

        {showPaymentModal && selectedPatron && (
          <PaymentSubmissionModal
            isOpen={showPaymentModal}
            selectedPatron={selectedPatron}
            onClose={this.closePaymentModal}
            onPaymentSubmit={this.handlePaymentSubmit}
            onLoanStatusUpdate={this.handleLoanStatusUpdate}
            onDataRefresh={this.syncAndFetchFines}
          />
        )}

        <OverdueSlipModal
          isOpen={showOverdueSlipModal}
          selectedPatron={selectedPatronForSlip}
          onClose={this.closeOverdueSlipModal}
          fineRatePerDay={FINE_RATE_PER_DAY}
          existingOverrideData={null}
          allowOverride
        />

        <PaymentHistoryModal
          isOpen={showPaymentHistoryModal}
          selectedPatron={selectedPatronForHistory}
          onClose={this.closePaymentHistoryModal}
          overrideData={
            selectedPatronForHistory
              ? patronOverrideData[selectedPatronForHistory.pid]
              : null
          }
        />
      </Container>
    );
  }
}

export default FineManagementList;
